/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Message,
  InputField,
  AddPageShell,
  AddSection,
} from "../components/Component";
import { codeOk, codeCreate, errorMessage } from "../../lib/index";
import EventsList from "./EventsList";
import AttractionsList from "./AttractionsList";
import { usePageContext } from "../../context/PageContext";
import { getOnePage } from "../../lib/sysNavigation/SysNavigation";
import { createPortal } from "react-dom";
import {
  FiEdit3,
  FiLayers,
  FiCalendar,
  FiMapPin,
  FiTrash2,
  FiX,
  FiCheck,
  FiInfo,
} from "react-icons/fi";

const Edit = () => {
  const memory = getOnePage();
  const { updatePage } = usePageContext();
  const [category, setCategory] = useState<any>(memory.category);
  const [error, setError] = useState<any>(null);
  const [status, setStatus] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setErrorMessage] = useState<any>(null);
  const [menu, setMenu] = useState<"event" | "attraction">("event");
  const [pages, setPages] = useState<any[]>([]);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{
    open: boolean;
    slug: any;
    title: string;
  }>({ open: false, slug: null, title: "" });
  const pageRoutes = "/page";

  useEffect(() => {
    (setPages as any)(
      ...memory.localPages.map((content: any) =>
        content.filter((val: any) => val.category === memory.category),
      ),
    );
  }, []);

  const cleanFields = () => {
    setSuccess(true);
    setLoading(false);
    setCategory(null);
  };

  const responses = (isError: any, st: any, msg: any) => {
    setError(isError);
    setStatus(st);
    setErrorMessage(msg);
  };

  const requestSubmit = (e: any) => {
    e.preventDefault();
    setConfirmSave(true);
  };

  const performSubmit = async () => {
    setLoading(true);
    if (!category) {
      setLoading(false);
      return responses(true, 400, "Page Title is Required");
    }
    if (pages.length === 0) {
      setLoading(false);
      return responses(true, 400, "Add Content To Page");
    }
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const filter = pages.map((val: any) => {
      return {
        description: val.description,
        hashtag: val.hashtag,
        image_cdn_id: val.image_cdn_id,
        slug: val.slug,
        status: val.status,
        thumbnail: val.thumbnail,
        title: val.title,
      };
    });
    const data = {
      category: category,
      old_category: memory.category,
      data: filter,
    };

    const response = await updatePage(data);
    if (response) {
      if (
        response.data.status === codeOk ||
        response.data.status === codeCreate
      ) {
        cleanFields();
        return responses(true, response.data.status, response.data.message);
      } else {
        setLoading(false);
        return responses(true, response.data.status, response.data.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <AddPageShell
      backRoute={pageRoutes}
      eyebrow="Composer • Pages"
      title="Edit Page"
      subtitle="Update a page by adjusting events or attractions in this landing."
      icon={<FiEdit3 className="h-5 w-5" />}
      pills={[
        { label: "Category", value: memory.category || "—" },
        { label: "Items", value: pages.length },
      ]}
      size="xl"
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={requestSubmit}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage("");
      }}
      message={
        <Message
          props={{
            error,
            success,
            message,
            loading,
            status,
            onClick: () => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            },
          }}
        />
      }
      modals={
        <>
          <ConfirmModal
            open={confirmSave}
            title="Save changes to this page?"
            description="Your updates will be applied to the selected category."
            onCancel={() => setConfirmSave(false)}
            onConfirm={() => {
              setConfirmSave(false);
              performSubmit();
            }}
            confirmText="Save"
          />
          <ConfirmModal
            open={confirmRemove.open}
            title="Remove content from page?"
            description={`This will remove “${confirmRemove.title}” from the current page.`}
            onCancel={() =>
              setConfirmRemove({ open: false, slug: null, title: "" })
            }
            onConfirm={() => {
              setPages(
                pages.filter((val: any) => val.slug !== confirmRemove.slug),
              );
              setConfirmRemove({ open: false, slug: null, title: "" });
            }}
            confirmText="Remove"
            tone="danger"
          />
        </>
      }
    >
      <AddSection
        title="Page Title"
        hint="This becomes the heading on the public page."
        icon={<FiEdit3 className="h-4 w-4" />}
      >
        <InputField
          props={{
            id: "page",
            title: "Page Title",
            type: "text",
            required: true,
            value: category ? category : "",
            onChange: (e: any) => {
              setCategory(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <AddSection
          title="Page Content"
          hint="Items currently in this page. Use the picker on the right to add or remove."
          icon={<FiLayers className="h-4 w-4" />}
        >
          {pages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-6 text-center">
              <p className="text-xs font-semibold text-gray-900">
                No content yet
              </p>
              <p className="mt-1 text-[11px] text-gray-500">
                Use the selectors on the right to add events or attractions.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {pages.map((value: any, index: number) => (
                <PageContent
                  slug={value.slug}
                  index={index}
                  title={value.title}
                  key={`${value.slug}-${index}`}
                  onAskRemove={(slug: any, title: string) =>
                    setConfirmRemove({ open: true, slug, title })
                  }
                />
              ))}
            </ul>
          )}
        </AddSection>

        <AddSection
          title="Add Items"
          hint='Switch between Events and Attractions, then "Add to Page".'
          icon={<FiCalendar className="h-4 w-4" />}
        >
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <button
              type="button"
              onClick={() => setMenu("event")}
              className={[
                "inline-flex items-center gap-2 rounded-xl px-4 py-2 transition",
                menu === "event"
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200",
              ].join(" ")}
            >
              <FiCalendar className="h-4 w-4" />
              Events
            </button>
            <button
              type="button"
              onClick={() => setMenu("attraction")}
              className={[
                "inline-flex items-center gap-2 rounded-xl px-4 py-2 transition",
                menu === "attraction"
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200",
              ].join(" ")}
            >
              <FiMapPin className="h-4 w-4" />
              Attractions
            </button>
          </div>
          {menu === "event" ? (
            <EventsList setPages={setPages} pages={pages} />
          ) : (
            <AttractionsList setPages={setPages} pages={pages} />
          )}
        </AddSection>
      </div>
    </AddPageShell>
  );
};

export default Edit;

const PageContent = ({
  index,
  title,
  slug,
  onAskRemove,
}: {
  index: number;
  title: string;
  slug: any;
  onAskRemove: (slug: any, title: string) => void;
}) => {
  return (
    <li className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gray-900 text-[10px] font-bold text-white">
          {String(title).slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900">
            {index + 1}. {title}
          </div>
          <div className="truncate text-[11px] text-gray-500">{slug}</div>
        </div>
        <button
          type="button"
          onClick={() => onAskRemove(slug, title)}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        >
          <FiTrash2 className="h-4 w-4" />
          Remove
        </button>
      </div>
    </li>
  );
};

const ConfirmModal = ({
  open,
  title,
  description,
  onCancel,
  onConfirm,
  confirmText = "Proceed",
  tone = "primary",
}: any) => {
  if (!open) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-desc"
      className="fixed inset-0 z-[2147483647] isolate flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white/95 shadow-2xl ring-1 ring-black/5 supports-[backdrop-filter]:bg-white/85">
        <div className="relative px-5 pt-5">
          <div className="flex items-start gap-3">
            <span
              className={[
                "grid h-9 w-9 place-items-center rounded-lg",
                tone === "danger"
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700",
              ].join(" ")}
            >
              {tone === "danger" ? (
                <FiTrash2 className="h-5 w-5" />
              ) : (
                <FiInfo className="h-5 w-5" />
              )}
            </span>
            <div>
              <h3
                id="confirm-title"
                className="text-base font-semibold text-gray-900"
              >
                {title}
              </h3>
              {description ? (
                <p id="confirm-desc" className="mt-1 text-sm text-gray-600">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pb-5 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <FiX className="h-4 w-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={[
              "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white shadow focus:outline-none focus-visible:ring-2",
              tone === "danger"
                ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
                : "bg-gray-900 hover:bg-black focus-visible:ring-brand",
            ].join(" ")}
          >
            <FiCheck className="h-4 w-4" />
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
